import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Download, Package, Search, Upload } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TablePaginationBar } from '@/components/shared/TablePaginationBar';
import { DEFAULT_TABLE_PAGE_SIZE } from '@/components/shared/tablePaginationConstants';
import { productMasterService, type PackagingStatusFilter, type ProductMasterListItem, type ProductPackagingRow, type ProductSheet } from './productMasterService';
import { useFeatureVisibility } from '@/contexts/FeatureVisibilityContext';

const inputClass =
  'mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-stocker-primary focus:outline-none focus:ring-1 focus:ring-stocker-primary';
const btnPrimaryClass =
  'inline-flex items-center justify-center rounded-md bg-stocker-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50';
const btnGhostClass =
  'inline-flex items-center gap-1.5 text-sm font-medium text-stocker-primary hover:underline';

type SupplierRow = {
  supplier_id: number;
  supplier_name?: string;
  warehouse_ids?: number[];
  moq?: number;
  mp?: number;
  is_primary?: boolean;
  moq_is_manual?: boolean;
  mp_is_manual?: boolean;
};

const PRIMARY_RULES = [
  'lowest_cost',
  'last_purchase',
  'has_price_tiers',
  'lowest_tier_price',
] as const;

type PrimaryRule = (typeof PRIMARY_RULES)[number];
type PrimaryMode = 'manual' | 'auto';

function parsePkgNumber(raw: string): number | null {
  const normalized = raw.trim().replace(',', '.');
  if (!normalized) return null;
  const n = Number(normalized);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function packagingDimensionMm(value: unknown, dimensionUnit?: string | null): string {
  if (value == null || value === '') return '';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return String(value);
  const mm = dimensionUnit === 'cm' ? parsed * 10 : parsed;
  return String(Number(mm.toFixed(4)));
}

/** Caja en mm → volumen unitario en m³, igual que la capacidad del contenedor. */
function computeUnitVolumeM3(
  lengthRaw: string,
  widthRaw: string,
  heightRaw: string
): number | null {
  const length = parsePkgNumber(lengthRaw);
  const width = parsePkgNumber(widthRaw);
  const height = parsePkgNumber(heightRaw);
  if (length == null || width == null || height == null) return null;
  return (length * width * height) / 1_000_000_000;
}

function formatVolumeM3(value: number): string {
  const fixed = value.toFixed(9).replace(/\.?0+$/, '');
  return fixed || '0';
}

function errorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (e && typeof e === 'object' && 'message' in e) {
    const msg = (e as { message?: unknown; detail?: unknown }).message;
    const detail = (e as { detail?: unknown }).detail;
    if (typeof msg === 'string' && msg.trim()) return msg;
    if (typeof detail === 'string' && detail.trim()) return detail;
  }
  return String(e);
}

function resolvePrimarySupplierId(data: ProductSheet): number | null {
  if (data.supplier_id) return data.supplier_id;
  const rows = (data.suppliers || []) as SupplierRow[];
  const primary = rows.find((s) => s.is_primary);
  return primary?.supplier_id ?? rows[0]?.supplier_id ?? null;
}

function resolveMoqSupplierId(data: ProductSheet, primaryId: number | null): number | null {
  const rows = (data.suppliers || []) as SupplierRow[];
  if (primaryId && rows.some((s) => s.supplier_id === primaryId)) return primaryId;
  return rows[0]?.supplier_id ?? null;
}

export function ProductMasterListPage() {
  const { t } = useTranslation();
  const { isActionEnabled } = useFeatureVisibility();
  const packagingEnabled = isActionEnabled('product_master.packaging', false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [packagingStatus, setPackagingStatus] = useState<PackagingStatusFilter>('all');
  const [items, setItems] = useState<ProductMasterListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);
  const [listTick, setListTick] = useState(0);
  const inputFileRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, packagingStatus, pageSize]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    productMasterService
      .list({
        search: debouncedSearch,
        packagingStatus: packagingEnabled ? packagingStatus : 'all',
        page,
        pageSize,
      })
      .then((res) => {
        if (!cancelled) {
          setItems(res.results);
          setTotalCount(res.count);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setItems([]);
          setTotalCount(0);
          setError(errorMessage(e));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, packagingStatus, packagingEnabled, page, pageSize, listTick]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const pageSafe = Math.min(page, totalPages);

  const onUploadPackaging = async (file: File | null) => {
    if (!file) return;
    setUploading(true);
    setUploadMessage(null);
    setError(null);
    try {
      const res = await productMasterService.uploadPackagingExcel(file);
      const errCount = res.errors?.length || 0;
      setUploadMessage(
        t('productMaster.packagingUploadDone', {
          created: res.created,
          updated: res.updated,
          skipped: res.skipped_manual,
          errors: errCount,
        }),
      );
      setListTick((n) => n + 1);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setUploading(false);
      if (inputFileRef.current) inputFileRef.current.value = '';
    }
  };

  return (
    <div className="w-full space-y-6">
      <div className="flex items-center justify-between gap-3">
        <Link to="/settings" className={btnGhostClass}>
          <ArrowLeft className="h-4 w-4" />
          {t('productMaster.backToSettings')}
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Search className="h-5 w-5 text-stocker-primary" />
            {t('productMaster.searchTitle')}
          </CardTitle>
          <CardDescription>{t('productMaster.searchHelp')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1">
              <input
                className={inputClass}
                placeholder={t('productMaster.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {packagingEnabled ? (
              <div className="w-full sm:w-56">
                <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
                  {t('productMaster.filterPackagingStatus')}
                </label>
                <select
                  className={inputClass}
                  value={packagingStatus}
                  onChange={(e) => setPackagingStatus(e.target.value as PackagingStatusFilter)}
                >
                  <option value="all">{t('productMaster.filterPackagingAll')}</option>
                  <option value="incomplete">{t('productMaster.filterPackagingIncomplete')}</option>
                  <option value="complete">{t('productMaster.filterPackagingComplete')}</option>
                </select>
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Package className="h-5 w-5 text-stocker-primary" />
                {t('productMaster.listTitle')}
              </CardTitle>
              <CardDescription>
                {loading ? t('common.loading') : `${totalCount} ${t('productMaster.results')}`}
              </CardDescription>
            </div>
            {packagingEnabled ? (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
                  disabled={uploading}
                  onClick={() => {
                    void productMasterService
                      .downloadPackagingTemplate()
                      .then(() => {
                        setUploadMessage(t('productMaster.packagingTemplateDownloaded'));
                      })
                      .catch((e) => setError(errorMessage(e)));
                  }}
                >
                  <Download className="h-4 w-4" />
                  {t('productMaster.packagingTemplate')}
                </button>
                <input
                  ref={inputFileRef}
                  type="file"
                  accept=".xlsx,.xlsm"
                  className="hidden"
                  onChange={(e) => void onUploadPackaging(e.target.files?.[0] || null)}
                />
                <button
                  type="button"
                  className={btnPrimaryClass}
                  disabled={uploading}
                  onClick={() => inputFileRef.current?.click()}
                >
                  <Upload className="mr-1.5 inline h-4 w-4" />
                  {uploading ? t('productMaster.packagingUploading') : t('productMaster.packagingUpload')}
                </button>
              </div>
            ) : null}
          </div>
          {uploadMessage ? (
            <p className="mt-2 text-sm text-emerald-800">{uploadMessage}</p>
          ) : null}
        </CardHeader>
        <CardContent className="!p-0 overflow-hidden rounded-b-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 bg-white">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t('productMaster.code')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t('productMaster.name')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t('productMaster.family')}
                  </th>
                  {packagingEnabled ? (
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      {t('productMaster.status')}
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {!loading && items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={packagingEnabled ? 4 : 3}
                      className="px-4 py-8 text-center text-sm text-gray-500"
                    >
                      {t('productMaster.empty')}
                    </td>
                  </tr>
                ) : (
                  items.map((item, index) => (
                    <tr
                      key={item.id}
                      className={`cursor-pointer ${
                        index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                      } hover:bg-blue-50/50`}
                      onClick={() => navigate(`/settings/products/${item.id}`)}
                    >
                      <td className="whitespace-nowrap px-4 py-3 font-mono text-sm font-semibold text-gray-900">
                        {item.code}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-800">{item.name}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                        {item.family_name || '—'}
                      </td>
                      {packagingEnabled ? (
                        <td className="whitespace-nowrap px-4 py-3">
                          {item.chips?.packaging_incomplete ? (
                            <span className="inline-flex rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-200">
                              {t('productMaster.chipPackaging')}
                            </span>
                          ) : (
                            <span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800 ring-1 ring-inset ring-emerald-200">
                              {t('productMaster.chipOk')}
                            </span>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!loading && totalCount > 0 ? (
            <div className="border-t border-gray-200 px-3 pb-3">
              <TablePaginationBar
                currentPage={pageSafe}
                totalPages={totalPages}
                totalItems={totalCount}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

export function ProductMasterDetailPage() {
  const { t } = useTranslation();
  const { isActionEnabled, isRouteEnabled } = useFeatureVisibility();
  const packagingEnabled = isActionEnabled('product_master.packaging', false);
  const canOpenLogisticsMasters = isRouteEnabled('/settings/logistics', false);
  const { productId } = useParams();
  const [sheet, setSheet] = useState<ProductSheet | null>(null);
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [moq, setMoq] = useState('');
  const [mp, setMp] = useState('');
  const [moqMpLocked, setMoqMpLocked] = useState(false);
  const [selectedSupplierId, setSelectedSupplierId] = useState<number | null>(null);
  const [primarySupplierId, setPrimarySupplierId] = useState<number | null>(null);
  const [primaryMode, setPrimaryMode] = useState<PrimaryMode>('manual');
  const [primaryRule, setPrimaryRule] = useState<PrimaryRule>('lowest_cost');
  const [pkgLength, setPkgLength] = useState('');
  const [pkgWidth, setPkgWidth] = useState('');
  const [pkgHeight, setPkgHeight] = useState('');
  const [pkgWeight, setPkgWeight] = useState('');
  const [pkgUnitsPerBox, setPkgUnitsPerBox] = useState('');
  const [pkgTempZone, setPkgTempZone] = useState('');
  const [pkgFragile, setPkgFragile] = useState(false);
  const [pkgStackable, setPkgStackable] = useState(true);
  const [pkgMaxStack, setPkgMaxStack] = useState('');
  const [pkgLocked, setPkgLocked] = useState(false);
  // El packaging se guarda por proveedor: se edita el del proveedor elegido o, si se marca, el de todos.
  const [pkgSupplierId, setPkgSupplierId] = useState<number | null>(null);
  const [pkgApplyAll, setPkgApplyAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedOk, setSavedOk] = useState(false);

  const applyPackagingFields = (pkg?: ProductPackagingRow | null) => {
    setPkgLength(packagingDimensionMm(pkg?.length, pkg?.dimension_unit));
    setPkgWidth(packagingDimensionMm(pkg?.width, pkg?.dimension_unit));
    setPkgHeight(packagingDimensionMm(pkg?.height, pkg?.dimension_unit));
    setPkgWeight(pkg?.weight != null ? String(pkg.weight) : '');
    setPkgUnitsPerBox(pkg?.units_per_box != null ? String(pkg.units_per_box) : '');
    setPkgTempZone(pkg?.temperature_zone || '');
    setPkgFragile(Boolean(pkg?.fragile));
    setPkgStackable(pkg?.stackable !== false);
    setPkgMaxStack(pkg?.max_stack_pallets != null ? String(pkg.max_stack_pallets) : '');
    setPkgLocked(Boolean(pkg?.is_manual));
  };

  const applySheet = (data: ProductSheet) => {
    setSheet(data);
    setName(data.name || '');
    setIsActive(Boolean(data.is_active));
    const primaryId = resolvePrimarySupplierId(data);
    setPrimarySupplierId(primaryId);
    const moqId = resolveMoqSupplierId(data, primaryId);
    setSelectedSupplierId(moqId);
    const rows = (data.suppliers || []) as SupplierRow[];
    const moqRow = rows.find((s) => s.supplier_id === moqId);
    setMoq(String(moqRow?.moq ?? ''));
    setMp(String(moqRow?.mp ?? ''));
    setMoqMpLocked(Boolean(moqRow?.moq_is_manual || moqRow?.mp_is_manual));
    const pkgRows = data.packaging_by_supplier || [];
    // Por defecto, el primer proveedor que ya tiene datos (el que usa la estimación); si ninguno, el primero.
    const pkgRow = pkgRows.find((row) => row.supplier_id === pkgSupplierId)
      ?? pkgRows.find((row) => row.equivalence_id != null)
      ?? pkgRows[0]
      ?? null;
    setPkgSupplierId(pkgRow?.supplier_id ?? null);
    applyPackagingFields(pkgRow);
    const rule = data.primary_supplier_rule;
    if (rule && (PRIMARY_RULES as readonly string[]).includes(rule)) {
      setPrimaryMode('auto');
      setPrimaryRule(rule as PrimaryRule);
    } else {
      setPrimaryMode('manual');
    }
  };

  const [loadingSheet, setLoadingSheet] = useState(true);

  const load = () => {
    if (!productId) return;
    setLoadingSheet(true);
    setError(null);
    productMasterService
      .get(Number(productId))
      .then(applySheet)
      .catch((e) => {
        setSheet(null);
        setError(errorMessage(e));
      })
      .finally(() => setLoadingSheet(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const liveUnitVolumeM3 = useMemo(
    () => computeUnitVolumeM3(pkgLength, pkgWidth, pkgHeight),
    [pkgLength, pkgWidth, pkgHeight]
  );
  const pkgRowSelected = (sheet?.packaging_by_supplier || []).find((row) => row.supplier_id === pkgSupplierId) ?? null;
  // Sin medidas, el volumen que usa la estimación es el cargado en la fila (p. ej. desde el Excel logístico).
  const storedUnitVolumeM3 = pkgRowSelected?.effective_unit_volume_m3 != null ? Number(pkgRowSelected.effective_unit_volume_m3) : null;
  const shownUnitVolumeM3 = liveUnitVolumeM3 ?? (storedUnitVolumeM3 != null && Number.isFinite(storedUnitVolumeM3) ? storedUnitVolumeM3 : null);
  const volumeFromStoredValue = liveUnitVolumeM3 == null && shownUnitVolumeM3 != null;

  const saveAll = async () => {
    if (!productId || !sheet) return;
    setSaving(true);
    setError(null);
    setSavedOk(false);
    try {
      const identityBody: Record<string, unknown> = {
        section: 'identity',
        name,
      };
      if (isActive !== sheet.is_active) {
        identityBody.is_active = isActive;
      }
      await productMasterService.patch(Number(productId), identityBody);

      const wasAuto = Boolean(sheet.primary_supplier_rule);
      const primaryChanged =
        primarySupplierId != null && primarySupplierId !== (sheet.supplier_id ?? null);

      if (primaryMode === 'auto') {
        await productMasterService.patch(Number(productId), {
          section: 'supplier',
          primary_supplier_rule: primaryRule,
        });
      } else if (primarySupplierId && (wasAuto || primaryChanged)) {
        if (primarySupplierId !== selectedSupplierId) {
          await productMasterService.patch(Number(productId), {
            section: 'supplier',
            supplier_id: primarySupplierId,
            set_primary: true,
          });
        }
      }

      if (selectedSupplierId) {
        const rows = (sheet.suppliers || []) as SupplierRow[];
        const current = rows.find((s) => s.supplier_id === selectedSupplierId);
        const moqChanged = moq !== String(current?.moq ?? '');
        const mpChanged = mp !== String(current?.mp ?? '');
        const wasLocked = Boolean(current?.moq_is_manual || current?.mp_is_manual);
        const lockChanged = moqMpLocked !== wasLocked;
        const shouldPatchPurchase = moqChanged || mpChanged || lockChanged;
        const setPrimaryWithPurchase =
          primaryMode === 'manual' &&
          primarySupplierId === selectedSupplierId &&
          (wasAuto || primaryChanged);

        if (shouldPatchPurchase || setPrimaryWithPurchase) {
          const body: Record<string, unknown> = {
            section: 'supplier',
            supplier_id: selectedSupplierId,
          };
          if (moqChanged || mpChanged) {
            body.moq = moq === '' ? undefined : Number(moq);
            body.mp = mp === '' ? undefined : Number(mp);
          }
          // Explicit lock/unlock: unchecked → moq_is_manual/mp_is_manual false.
          if (lockChanged || moqChanged || mpChanged) {
            body.moq_is_manual = moqMpLocked;
            body.mp_is_manual = moqMpLocked;
          }
          if (setPrimaryWithPurchase) {
            body.set_primary = true;
          }
          await productMasterService.patch(Number(productId), body);
        }
      }

      const pkg = (sheet.packaging_by_supplier || []).find((row) => row.supplier_id === pkgSupplierId) ?? null;
      const pkgDimsChanged =
        packagingEnabled &&
        pkg != null &&
        (pkgLength !== packagingDimensionMm(pkg?.length, pkg?.dimension_unit) ||
          pkgWidth !== packagingDimensionMm(pkg?.width, pkg?.dimension_unit) ||
          pkgHeight !== packagingDimensionMm(pkg?.height, pkg?.dimension_unit) ||
          pkgWeight !== (pkg?.weight != null ? String(pkg.weight) : '') ||
          pkgUnitsPerBox !== (pkg?.units_per_box != null ? String(pkg.units_per_box) : '') ||
          pkgLocked !== Boolean(pkg?.is_manual) ||
          pkgTempZone !== (pkg?.temperature_zone || '') ||
          pkgFragile !== Boolean(pkg?.fragile) ||
          pkgStackable !== (pkg?.stackable !== false) ||
          pkgMaxStack !== (pkg?.max_stack_pallets != null ? String(pkg.max_stack_pallets) : ''));
      if (pkgDimsChanged) {
        await productMasterService.patch(Number(productId), {
          section: 'packaging',
          ...(pkgApplyAll ? {} : { supplier_id: pkgSupplierId }),
          length: pkgLength === '' ? null : pkgLength,
          width: pkgWidth === '' ? null : pkgWidth,
          height: pkgHeight === '' ? null : pkgHeight,
          dimension_unit: 'mm',
          weight: pkgWeight === '' ? null : pkgWeight,
          units_per_box: pkgUnitsPerBox === '' ? null : Number(pkgUnitsPerBox),
          is_manual: pkgLocked,
          temperature_zone: pkgTempZone,
          fragile: pkgFragile,
          stackable: pkgStackable,
          max_stack_pallets: pkgMaxStack === '' ? null : Number(pkgMaxStack),
        });
      }

      const data = await productMasterService.get(Number(productId));
      applySheet(data);
      setSavedOk(true);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (loadingSheet) {
    return <div className="text-sm text-gray-500">{t('common.loading')}</div>;
  }

  if (!sheet) {
    return (
      <div className="space-y-3">
        <Link to="/settings/products" className={btnGhostClass}>
          {t('productMaster.backToList')}
        </Link>
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error || t('common.error')}
        </div>
      </div>
    );
  }

  const suppliers = (sheet.suppliers || []) as SupplierRow[];

  const metaItems: Array<{ label: string; value: ReactNode }> = [
    { label: t('productMaster.code'), value: <span className="font-mono">{sheet.code || '—'}</span> },
    {
      label: t('productMaster.family'),
      value: (
        <>
          {sheet.family_name || '—'}
          {sheet.family_client_code ? (
            <span className="ml-1 font-mono text-xs text-gray-400">({sheet.family_client_code})</span>
          ) : null}
        </>
      ),
    },
    {
      label: t('productMaster.sellUnit'),
      value: `${sheet.sell_unit_name || '—'}${sheet.sell_unit_abbreviation ? ` (${sheet.sell_unit_abbreviation})` : ''}`,
    },
    {
      label: t('productMaster.buyUnit'),
      value: `${sheet.buy_unit_name || '—'}${sheet.buy_unit_abbreviation ? ` (${sheet.buy_unit_abbreviation})` : ''}`,
    },
    { label: t('productMaster.unitWeight'), value: sheet.peso_unitario || '—' },
  ];

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Link to="/settings/products" className={btnGhostClass}>
          <ArrowLeft className="h-4 w-4" />
          {t('productMaster.backToList')}
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`/settings/equivalences?product=${sheet.id}`}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            {t('productMaster.openEquivalences')}
          </Link>
          <button type="button" disabled={saving} className={btnPrimaryClass} onClick={saveAll}>
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{sheet.name}</h2>
        <p className="mt-1 text-sm text-gray-500">{t('productMaster.codeReadonly')}</p>
      </div>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      ) : null}
      {savedOk ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {t('productMaster.saved')}
        </div>
      ) : null}

      <div className="mx-auto w-full max-w-3xl space-y-4">
      <Card>
        <CardHeader className="mb-3 border-b border-gray-100 pb-3">
          <CardTitle className="text-base font-semibold text-gray-900">{t('productMaster.erpData')}</CardTitle>
          <CardDescription>{t('productMaster.erpDataHelp')}</CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {metaItems.map((item) => (
              <div key={item.label} className="min-w-0">
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{item.label}</dt>
                <dd className="mt-0.5 truncate text-sm text-gray-900">{item.value}</dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="mb-3 border-b border-gray-100 pb-3">
          <CardTitle className="text-base font-semibold text-gray-900">{t('productMaster.identity')}</CardTitle>
          <CardDescription>{t('productMaster.identityHelp')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              {t('productMaster.name')}
              <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="flex items-start gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-stocker-primary focus:ring-stocker-primary"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              <span>
                {t('productMaster.active')}
                <span className="mt-0.5 block text-xs font-normal text-gray-500">
                  {t('productMaster.activeHelp')}
                </span>
              </span>
            </label>
          </div>
        </CardContent>
      </Card>

      {packagingEnabled ? (
      <Card>
        <CardHeader className="mb-3 border-b border-gray-100 pb-3">
          <CardTitle className="text-base font-semibold text-gray-900">
            {t('productMaster.packaging')}
          </CardTitle>
          <CardDescription>{t('productMaster.packagingHelp')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {(sheet.packaging_by_supplier || []).length === 0 ? (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                {t('productMaster.packagingNoSuppliers')}
              </p>
            ) : (sheet.packaging_by_supplier || []).length > 1 ? (
              <div className="flex flex-wrap items-center gap-3">
                <label className="block text-sm font-medium text-gray-700">
                  {t('productMaster.packagingSupplier')}
                  <select
                    className={`${inputClass} mt-1`}
                    value={pkgSupplierId ?? ''}
                    onChange={(e) => {
                      const nextId = Number(e.target.value);
                      setPkgSupplierId(nextId);
                      applyPackagingFields((sheet.packaging_by_supplier || []).find((row) => row.supplier_id === nextId));
                    }}
                  >
                    {(sheet.packaging_by_supplier || []).map((row) => (
                      <option key={row.supplier_id} value={row.supplier_id}>
                        {row.supplier_name || row.supplier_id}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="mt-5 inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={pkgApplyAll}
                    onChange={(e) => setPkgApplyAll(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  {t('productMaster.packagingApplyAll')}
                </label>
              </div>
            ) : null}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <label className="block text-sm font-medium text-gray-700">
                {t('productMaster.packagingLength')}
                <input
                  className={inputClass}
                  inputMode="decimal"
                  value={pkgLength}
                  onChange={(e) => {
                    setPkgLength(e.target.value);
                    setPkgLocked(true);
                  }}
                />
              </label>
              <label className="block text-sm font-medium text-gray-700">
                {t('productMaster.packagingWidth')}
                <input
                  className={inputClass}
                  inputMode="decimal"
                  value={pkgWidth}
                  onChange={(e) => {
                    setPkgWidth(e.target.value);
                    setPkgLocked(true);
                  }}
                />
              </label>
              <label className="block text-sm font-medium text-gray-700">
                {t('productMaster.packagingHeight')}
                <input
                  className={inputClass}
                  inputMode="decimal"
                  value={pkgHeight}
                  onChange={(e) => {
                    setPkgHeight(e.target.value);
                    setPkgLocked(true);
                  }}
                />
              </label>
            </div>
            <p className="text-xs text-gray-500">{t('productMaster.packagingDimensionUnitHelp')}</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block text-sm font-medium text-gray-700">
                {t('productMaster.packagingWeight')}
                <input
                  className={inputClass}
                  inputMode="decimal"
                  value={pkgWeight}
                  onChange={(e) => {
                    setPkgWeight(e.target.value);
                    setPkgLocked(true);
                  }}
                />
              </label>
              <label className="block text-sm font-medium text-gray-700">
                {t('productMaster.packagingUnitsPerBox')}
                <input
                  className={inputClass}
                  inputMode="numeric"
                  value={pkgUnitsPerBox}
                  onChange={(e) => {
                    setPkgUnitsPerBox(e.target.value);
                    setPkgLocked(true);
                  }}
                />
              </label>
            </div>
            <div className="border-t border-gray-100 pt-4">
              <p className="text-sm font-medium text-gray-900">
                {t('productMaster.packagingHandling')}
              </p>
              <p className="mt-0.5 text-xs text-gray-500">{t('productMaster.packagingHandlingHelp')}</p>
              <div className="mt-3 space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  {t('productMaster.packagingTempZone')}
                  <select
                    className={inputClass}
                    value={pkgTempZone}
                    onChange={(e) => {
                      setPkgTempZone(e.target.value);
                      setPkgLocked(true);
                    }}
                  >
                    <option value="">{t('productMaster.packagingTempZoneUnset')}</option>
                    <option value="AMBIENT">{t('productMaster.packagingTempZoneAmbient')}</option>
                    <option value="CHILLED">{t('productMaster.packagingTempZoneChilled')}</option>
                    <option value="FROZEN">{t('productMaster.packagingTempZoneFrozen')}</option>
                  </select>
                </label>
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-stocker-primary focus:ring-stocker-primary"
                    checked={pkgFragile}
                    onChange={(e) => {
                      setPkgFragile(e.target.checked);
                      setPkgLocked(true);
                    }}
                  />
                  <span>
                    {t('productMaster.packagingFragile')}
                    <span className="mt-0.5 block text-xs font-normal text-gray-500">
                      {t('productMaster.packagingFragileHelp')}
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-stocker-primary focus:ring-stocker-primary"
                    checked={pkgStackable}
                    onChange={(e) => {
                      setPkgStackable(e.target.checked);
                      setPkgLocked(true);
                    }}
                  />
                  <span>
                    {t('productMaster.packagingStackable')}
                    <span className="mt-0.5 block text-xs font-normal text-gray-500">
                      {t('productMaster.packagingStackableHelp')}
                    </span>
                  </span>
                </label>
                {pkgStackable ? (
                  <label className="block text-sm font-medium text-gray-700">
                    {t('productMaster.packagingMaxStack')}
                    <input
                      className={inputClass}
                      inputMode="numeric"
                      value={pkgMaxStack}
                      onChange={(e) => {
                        setPkgMaxStack(e.target.value);
                        setPkgLocked(true);
                      }}
                    />
                  </label>
                ) : null}
              </div>
            </div>
            <div className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {t('productMaster.packagingVolume')}
              </p>
              <p className="mt-0.5 font-medium text-gray-900">
                {shownUnitVolumeM3 != null ? `${formatVolumeM3(shownUnitVolumeM3)} m³` : '—'}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                {volumeFromStoredValue ? t('productMaster.packagingVolumeStored') : t('productMaster.packagingVolumeHelp')}
              </p>
            </div>
            {pkgRowSelected ? (
              <div className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  {t('productMaster.packagingPalletTitle')}
                </p>
                {pkgRowSelected.boxes_per_layer != null || pkgRowSelected.layers_per_pallet != null || pkgRowSelected.units_per_pallet != null || pkgRowSelected.pallet_length_mm != null ? (
                  <dl className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
                    {([
                      ['packagingPalletBoxesPerLayer', pkgRowSelected.boxes_per_layer],
                      ['packagingPalletLayers', pkgRowSelected.layers_per_pallet],
                      ['packagingPalletUnits', pkgRowSelected.units_per_pallet],
                      ['packagingPalletFamily', pkgRowSelected.pallet_family || null],
                      ['packagingPalletLength', pkgRowSelected.pallet_length_mm],
                      ['packagingPalletWidth', pkgRowSelected.pallet_width_mm],
                      ['packagingPalletHeight', pkgRowSelected.pallet_loaded_height_mm],
                      ['packagingPalletTare', pkgRowSelected.pallet_tare_weight_kg],
                      ['packagingPalletMaxLoad', pkgRowSelected.pallet_max_top_load_kg],
                    ] as const).map(([key, value]) => (
                      <div key={key}>
                        <dt className="text-gray-500">{t(`productMaster.${key}`)}</dt>
                        <dd className="font-medium text-gray-900">{value == null || value === '' ? '—' : String(value)}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="mt-0.5 text-xs text-gray-500">{t('productMaster.packagingPalletEmpty')}</p>
                )}
                {canOpenLogisticsMasters ? (
                  <Link
                    to={`/settings/logistics/masters?supplier=${pkgRowSelected.supplier_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-block text-xs font-medium text-stocker-primary hover:underline"
                  >
                    {t('productMaster.packagingPalletEdit')}
                  </Link>
                ) : null}
              </div>
            ) : null}
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-stocker-primary focus:ring-stocker-primary"
                checked={pkgLocked}
                onChange={(e) => setPkgLocked(e.target.checked)}
              />
              <span>
                {t('productMaster.packagingLock')}
                <span className="mt-0.5 block text-xs font-normal text-gray-500">
                  {t('productMaster.packagingLockHelp')}
                </span>
              </span>
            </label>
          </div>
        </CardContent>
      </Card>
      ) : null}

      <Card>
        <CardHeader className="mb-3 border-b border-gray-100 pb-3">
          <CardTitle className="text-base font-semibold text-gray-900">
            {t('productMaster.primarySupplier')}
          </CardTitle>
          <CardDescription>{t('productMaster.primarySupplierSectionHelp')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-4 text-sm text-gray-700">
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="primaryMode"
                  className="h-4 w-4 border-gray-300 text-stocker-primary focus:ring-stocker-primary"
                  checked={primaryMode === 'manual'}
                  onChange={() => setPrimaryMode('manual')}
                />
                {t('productMaster.primaryModeManual')}
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="primaryMode"
                  className="h-4 w-4 border-gray-300 text-stocker-primary focus:ring-stocker-primary"
                  checked={primaryMode === 'auto'}
                  onChange={() => setPrimaryMode('auto')}
                />
                {t('productMaster.primaryModeAuto')}
              </label>
            </div>

            {primaryMode === 'manual' ? (
              <label className="block text-sm font-medium text-gray-700">
                {t('productMaster.primarySupplier')}
                <select
                  className={inputClass}
                  value={primarySupplierId ?? ''}
                  onChange={(e) => setPrimarySupplierId(e.target.value ? Number(e.target.value) : null)}
                >
                  {suppliers.length === 0 ? (
                    <option value="">{t('productMaster.noSuppliers')}</option>
                  ) : (
                    suppliers.map((s) => (
                      <option key={s.supplier_id} value={s.supplier_id}>
                        {s.supplier_name || s.supplier_id}
                      </option>
                    ))
                  )}
                </select>
                <span className="mt-1 block text-xs font-normal text-gray-500">
                  {t('productMaster.primarySupplierHelp')}
                </span>
              </label>
            ) : (
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                  {t('productMaster.primaryRule')}
                  <select
                    className={inputClass}
                    value={primaryRule}
                    onChange={(e) => setPrimaryRule(e.target.value as PrimaryRule)}
                  >
                    {PRIMARY_RULES.map((rule) => (
                      <option key={rule} value={rule}>
                        {t(`productMaster.primaryRule_${rule}`)}
                      </option>
                    ))}
                  </select>
                  <span className="mt-1 block text-xs font-normal text-gray-500">
                    {t('productMaster.primaryRuleHelp')}
                  </span>
                </label>
                {(() => {
                  const preview = sheet.primary_rule_previews?.[primaryRule] ?? null;
                  const previewName = preview?.supplier_name || null;
                  const currentName =
                    sheet.supplier_name ||
                    suppliers.find((s) => s.supplier_id === sheet.supplier_id)?.supplier_name ||
                    null;
                  const previewDiffers =
                    preview?.supplier_id != null &&
                    sheet.supplier_id != null &&
                    preview.supplier_id !== sheet.supplier_id;
                  return (
                    <div className="space-y-2 rounded-md border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                          {t('productMaster.primaryRuleResult')}
                        </p>
                        <p className="mt-0.5 font-medium text-gray-900">
                          {previewName || t('productMaster.primaryRuleNone')}
                        </p>
                      </div>
                      {currentName && (previewDiffers || !previewName) ? (
                        <p className="text-xs text-gray-500">
                          {t('productMaster.primaryRuleCurrent')}: {currentName}
                        </p>
                      ) : null}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="mb-3 border-b border-gray-100 pb-3">
          <CardTitle className="text-base font-semibold text-gray-900">{t('productMaster.purchase')}</CardTitle>
          <CardDescription>{t('productMaster.purchaseHelp')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              {t('productMaster.supplier')}
              <select
                className={inputClass}
                value={selectedSupplierId ?? ''}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  setSelectedSupplierId(id);
                  const row = suppliers.find((s) => s.supplier_id === id);
                  if (row) {
                    setMoq(String(row.moq ?? ''));
                    setMp(String(row.mp ?? ''));
                    setMoqMpLocked(Boolean(row.moq_is_manual || row.mp_is_manual));
                  }
                }}
              >
                {suppliers.length === 0 ? (
                  <option value="">{t('productMaster.noSuppliers')}</option>
                ) : (
                  suppliers.map((s) => (
                    <option key={s.supplier_id} value={s.supplier_id}>
                      {s.supplier_name || s.supplier_id}
                    </option>
                  ))
                )}
              </select>
              <span className="mt-1 block text-xs font-normal text-gray-500">{t('productMaster.moqMpHelp')}</span>
            </label>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="block text-sm font-medium text-gray-700">
                MOQ
                <input
                  className={inputClass}
                  value={moq}
                  onChange={(e) => {
                    setMoq(e.target.value);
                    setMoqMpLocked(true);
                  }}
                />
              </label>
              <label className="block text-sm font-medium text-gray-700">
                MP
                <input
                  className={inputClass}
                  value={mp}
                  onChange={(e) => {
                    setMp(e.target.value);
                    setMoqMpLocked(true);
                  }}
                />
              </label>
            </div>

            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-stocker-primary focus:ring-stocker-primary"
                checked={moqMpLocked}
                onChange={(e) => setMoqMpLocked(e.target.checked)}
              />
              <span>
                {t('productMaster.moqMpLock')}
                <span className="mt-0.5 block text-xs font-normal text-gray-500">
                  {t('productMaster.moqMpLockHelp')}
                </span>
              </span>
            </label>
          </div>
        </CardContent>
      </Card>
      </div>

      <div className="flex justify-end">
        <button type="button" disabled={saving} className={btnPrimaryClass} onClick={saveAll}>
          {saving ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </div>
  );
}
